interface DailySchedule {
  type: 'every_n_days' | 'weekdays'
  days?: number
  startTime: string
}

interface WeeklySchedule {
  days: number[]
  startTime: string
}

export const convertDailyToCron = (schedule: DailySchedule): { cron: string; readable: string } => {
  const [hours, minutes] = schedule.startTime.split(':')

  if (schedule.type === 'weekdays') {
    return {
      cron: `${formatTimeComponent(minutes)} ${formatTimeComponent(hours)} * * 1-5`,
      readable: `Runs at ${schedule.startTime} every weekday (Monday through Friday)`,
    }
  }

  return {
    cron: `${formatTimeComponent(minutes)} ${formatTimeComponent(hours)} */${schedule.days} * *`,
    readable: `Runs at ${schedule.startTime} every ${schedule.days} day(s)`,
  }
}

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const formatTimeComponent = (value: string | number): string => String(value).padStart(2, '0')

export const convertWeeklyToCron = (
  schedule: WeeklySchedule
): { cron: string; readable: string } => {
  const [hours, minutes] = schedule.startTime.split(':')
  const sortedDays = [...schedule.days].sort((a, b) => a - b)
  const dayNumbers = sortedDays.map(day => formatTimeComponent(day))
  const dayOfWeekNames = sortedDays.map(day => dayNames[day])

  return {
    cron: `${formatTimeComponent(minutes)} ${formatTimeComponent(hours)} * * ${dayNumbers.join(
      ','
    )}`,
    readable: `Runs at ${schedule.startTime} every ${dayOfWeekNames.join(', ')}`,
  }
}

export const parseCustomCron = (cronExpression: string): { readable: string } => {
  const parts = cronExpression.trim().split(/\s+/)
  if (parts.length !== 5) {
    return { readable: 'Invalid cron expression' }
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts
  const partsDesc: string[] = []

  if (hour === '*') {
    // Handle minutes when no specific hour
    if (minute === '*') {
      partsDesc.push('every minute')
    } else if (minute.includes('/')) {
      const [, freq] = minute.split('/')
      partsDesc.push(`every ${freq} minute(s)`)
    } else {
      partsDesc.push(`at minute ${minute} of every hour`)
    }
  } else if (hour.includes('/')) {
    const [, freq] = hour.split('/')
    if (minute === '*') {
      partsDesc.push(`every minute of every ${freq} hour(s)`)
    } else {
      partsDesc.push(`every ${freq} hour(s) at minute ${minute}`)
    }
  } else {
    partsDesc.push(`at ${formatTimeComponent(hour)}:${formatTimeComponent(minute)}`)
  }

  if (dayOfMonth === '*' && dayOfWeek === '*') {
    partsDesc.push('every day')
  } else if (dayOfWeek !== '*') {
    const days = dayOfWeek.split(',').map(d => {
      if (d === '1-5') return 'weekdays'
      if (d === '0,6') return 'weekends'
      if (d.includes('-')) {
        const [start, end] = d.split('-').map(num => parseInt(num))
        return `${dayNames[start]} through ${dayNames[end]}`
      }
      const num = parseInt(d)
      return dayNames[num]
    })
    partsDesc.push(`on ${days.join(', ')}`)
  } else if (dayOfMonth !== '*') {
    if (dayOfMonth.includes('/')) {
      const [, freq] = dayOfMonth.split('/')
      partsDesc.push(`every ${freq} day(s)`)
    } else if (dayOfMonth.includes('-')) {
      const [start, end] = dayOfMonth.split('-')
      partsDesc.push(`on days ${start} through ${end}`)
    } else {
      partsDesc.push(`on day ${dayOfMonth}`)
    }
  }

  if (month !== '*') {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const months = month.split(',').map(m => monthNames[parseInt(m) - 1])
    partsDesc.push(`in ${months.join(', ')}`)
  }

  return { readable: partsDesc.join(' ') }
}

/**
 * Validates if a cron expression runs less frequently than hourly
 * @param cronExpression - Standard 5-field cron expression (minute hour day month weekday)
 * @returns true if the cron runs hourly or less frequently, false if it runs more frequently than hourly
 */
interface ValidationResult {
  isValid: boolean
  error?: string
}

export const validateCronExpression = (cronExpression: string): ValidationResult => {
  const parts = cronExpression.trim().split(/\s+/)
  if (parts.length !== 5) {
    return { isValid: false, error: 'Invalid cron expression format' }
  }
  const [minutes, hours, days, months, dayOfWeek] = parts

  if (days.includes('*/')) {
    const stepValue = days.split('/')[1]
    if (isNaN(parseInt(stepValue))) {
      return { isValid: false, error: 'Invalid step value in days field' }
    }
  }
  const isValidNumber = (value: string, min: number, max: number) => {
    // Handle wildcards and steps
    if (value === '*' || value.includes('/')) return true

    // Handle comma-separated values
    if (value.includes(',')) {
      return value.split(',').every(part => isValidNumber(part, min, max))
    }

    // Handle ranges (e.g., "4-5")
    if (value.includes('-')) {
      const [start, end] = value.split('-')
      if (!/^\d+$/.test(start) || !/^\d+$/.test(end)) return false
      const startNum = parseInt(start)
      const endNum = parseInt(end)
      return startNum >= min && endNum <= max && startNum <= endNum
    }

    // Handle single numbers
    if (!/^\d+$/.test(value)) return false
    const num = parseInt(value)
    return num >= min && num <= max
  }

  if (
    !isValidNumber(minutes, 0, 59) ||
    !isValidNumber(hours, 0, 23) ||
    !isValidNumber(months, 1, 12) ||
    !isValidNumber(dayOfWeek, 0, 6)
  ) {
    return {
      isValid: false,
      error: 'Invalid minute (0-59) or hour (0-23) or month (1-12) or day of week (0-6)',
    }
  }

  // Check frequency
  const parseStep = (field: string) => {
    if (field === '*') return 1
    if (field.includes('/')) {
      const [, step] = field.split('/')
      if (!step || step === '' || isNaN(parseInt(step, 10))) {
        return -1
      }
      return parseInt(step, 10)
    }
    return 60
  }

  const minuteStep = parseStep(minutes)
  const hourStep = parseStep(hours)

  if (minuteStep === -1 || hourStep === -1) {
    return { isValid: false, error: 'Invalid value for minutes or hours in cron expression' }
  }

  if (minuteStep < 60 || hourStep < 1) {
    return { isValid: false, error: 'Schedule cannot run more frequently than hourly' }
  }

  return { isValid: true }
}

export const convertHourlyToCron = (
  mode: 'every' | 'specific',
  hours: string,
  minutes: string,
  specificTime: string
): { cron: string; readable: string } => {
  if (mode === 'specific') {
    const [hh, mm] = specificTime.split(':')
    return {
      cron: `${formatTimeComponent(mm)} ${formatTimeComponent(hh)} * * *`,
      readable: `Runs at ${specificTime} every day`,
    }
  }

  return {
    cron: `${formatTimeComponent(minutes)} */${formatTimeComponent(hours)} * * *`,
    readable: `Runs every ${hours} hour(s) at minute ${minutes}`,
  }
}
